import type {
  ApiApplicationStage,
  ApiPipelineStage,
  ApiTalentPoolReason,
} from '../../libs/api/pipelineService';

export const STAGES: ApiApplicationStage[] = [
  'NEW',
  'SCREENING',
  'INTERVIEW',
  'OFFER',
  'HIRED',
  'REJECTED',
  'TALENT_POOL',
];

export const PIPELINE_STAGES: ApiPipelineStage[] = [
  'NEW',
  'SCREENING',
  'INTERVIEW',
  'OFFER',
  'HIRED',
];

export const isPipelineStage = (stage: ApiApplicationStage): stage is ApiPipelineStage =>
  (PIPELINE_STAGES as ApiApplicationStage[]).includes(stage);

export const TALENT_POOL_REASON_LABELS: Record<ApiTalentPoolReason, string> = {
  JD_ON_HOLD: 'JD paused / on hold',
  POSITION_FILLED: 'Position already filled',
  BETTER_FIT_OTHER_ROLE: 'Better fit for another role',
  RECONNECT_LATER: 'Candidate asked to reconnect later',
  OTHER: 'Other',
};

export const STAGE_LABELS: Record<ApiApplicationStage, string> = {
  NEW: 'New',
  SCREENING: 'HR Approved',
  INTERVIEW: 'Interview',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  TALENT_POOL: 'Talent Pool',
};

/** Stage dot colour shared by column headers and the card status pill. */
export const STAGE_DOT_CLASS: Record<ApiApplicationStage, string> = {
  NEW: 'bg-muted-foreground',
  SCREENING: 'bg-info',
  INTERVIEW: 'bg-violet-500 dark:bg-violet-400',
  OFFER: 'bg-orange-500 dark:bg-orange-400',
  HIRED: 'bg-success',
  REJECTED: 'bg-destructive',
  TALENT_POOL: 'bg-violet-300 dark:bg-violet-300',
};

export type PipelineCard = {
  id: string;
  applicationId: string;
  candidateId: string;
  name: string;
  email: string;
  role: string;
  department: string | null;
  stage: ApiApplicationStage;
  score?: number;
  nextInterviewId?: string | null;
  nextInterviewResult?: 'PASSED' | 'FAILED' | null;
  nextInterviewRound?: string | null;
  appliedDate: string;
  kanbanNote?: string;
  noteId?: string;
  talentPoolReason?: ApiTalentPoolReason | null;
  talentPoolNote?: string | null;
};

export type ColumnState = Record<ApiApplicationStage, PipelineCard[]>;

export const createEmptyColumns = (): ColumnState => ({
  NEW: [],
  SCREENING: [],
  INTERVIEW: [],
  OFFER: [],
  HIRED: [],
  REJECTED: [],
  TALENT_POOL: [],
});

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();

export type PipelineBoardFilters = {
  search: string;
  jobTitles: string[];
  teams: string[];
};

export const isPipelineFilterActive = (filters: PipelineBoardFilters) =>
  filters.search.trim().length > 0 || filters.jobTitles.length > 0 || filters.teams.length > 0;

export const filterPipelineColumns = (
  columns: ColumnState,
  filters: PipelineBoardFilters,
): ColumnState => {
  if (!isPipelineFilterActive(filters)) {
    return columns;
  }

  const normalizedSearch = normalizeSearchText(filters.search);
  const next = createEmptyColumns();

  STAGES.forEach((stage) => {
    next[stage] = columns[stage].filter((card) => {
      const matchesSearch =
        !normalizedSearch ||
        normalizeSearchText(card.name).includes(normalizedSearch) ||
        normalizeSearchText(card.email).includes(normalizedSearch);

      const matchesJobTitle =
        filters.jobTitles.length === 0 || filters.jobTitles.includes(card.role);

      const matchesTeam =
        filters.teams.length === 0 ||
        (card.department != null && filters.teams.includes(card.department));

      return matchesSearch && matchesJobTitle && matchesTeam;
    });
  });

  return next;
};

export const collectUniqueValues = (
  columns: ColumnState,
  pick: (card: PipelineCard) => string | null | undefined,
) => {
  const values = new Set<string>();

  STAGES.forEach((stage) => {
    columns[stage].forEach((card) => {
      const value = pick(card);
      if (value) {
        values.add(value);
      }
    });
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
};

export const countCards = (columns: ColumnState) =>
  STAGES.reduce((total, stage) => total + columns[stage].length, 0);
